import { useEffect, useState } from 'react';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import * as Yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import { Button, Alert } from '../../components/primitives';
import { FormInput, FormCheckbox } from '../../components/form';
import { AuthLayout } from '../AuthLayout';

export default function SignIn() {
  const auth = useAuth();

  interface SignInValues {
    password: string;
    email: string;
    remember: boolean;
  }

  // useSearchParams decodes the value; the old raw split('=') prefilled the
  // URL-ENCODED email (user%2Bx%40y.com) after password reset, which then
  // failed Yup's email validation and blocked sign-in until retyped.
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const loginValidation = Yup.object().shape({
    email: Yup.string().email().required('An email address is required.'),
    password: Yup.string()
      .required('A password is required.')
      .min(8)
      .max(16)
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
        'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character'
      ),
  });

  const handleOnSubmit = async (values: SignInValues, actions: FormikHelpers<SignInValues>) => {
    auth &&
      (await auth
        .login({ username: values.email, password: values.password })
        .then(() => {
          actions.setSubmitting(false);
        })
        .catch((err) => {
          setSubmitError(err.message);
          actions.setSubmitting(false);
        }));
  };

  const handleOnChange = () => {
    auth?.resetErrorMessage();
    setSubmitError(undefined);
  };

  useEffect(() => {
    if (auth?.errorMessage) {
      setSubmitError(auth.errorMessage);
    }
  }, [auth?.errorMessage]);

  if (auth?.isLoggedIn) {
    return <AuthLayout title="Signed in">You are now signed in.</AuthLayout>;
  }

  return (
    <AuthLayout
      title="Sign in to your account"
      subtitle={
        <>
          Don't have an account?{' '}
          <Link to="/signup" className="font-medium text-primary hover:text-primary/80">
            Sign up
          </Link>
        </>
      }
      banner={
        submitError && (
          <Alert variant="destructive">
            {submitError}
          </Alert>
        )
      }
    >
      <Formik
        initialValues={{ email: email || '', password: '', remember: false }}
        onSubmit={handleOnSubmit}
        validationSchema={loginValidation}
      >
        {(props: FormikProps<SignInValues>) => (
          <Form onChange={handleOnChange} className="space-y-5">
            <FormInput label="Email" id="email" name="email" type="text" required disabled={props.isSubmitting} />
            <FormInput
              label="Password"
              id="password"
              name="password"
              type="password"
              required
              disabled={props.isSubmitting}
              labelRight={
                <Link to="/forgot-password" className="text-sm font-medium text-primary hover:text-primary/80">
                  Forgot your password?
                </Link>
              }
            />
            <FormCheckbox name="remember" label="Remember me" id="remember" disabled={props.isSubmitting} />
            <Button type="submit" className="w-full" disabled={props.isSubmitting}>
              {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
}
